import { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Grid,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  FormHelperText,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import { motion } from 'framer-motion';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteIcon from '@mui/icons-material/Delete';
import { submitDonationForm, sendDonationWorksheetPDF } from '../../services/api';
import { useNavigate } from 'react-router-dom';

const US_STATES = [
  'Alabama' ,
  'Alaska' ,
  'Arizona',
  'Arkansas',
  'California',
  'Colorado',
  'Connecticut',
  'Delaware',
  'District of Columbia',
  'Florida',
  'Georgia',
  'Hawaii',
  'Idaho',
  'Illinois',
  'Indiana',
  'Iowa',
  'Kansas',
  'Kentucky',
  'Louisiana',
  'Maine',
  'Maryland',
  'Massachusetts',
  'Michigan',
  'Minnesota',
  'Mississippi',
  'Missouri',
  'Montana',
  'Nebraska',
  'Nevada',
  'New Hampshire',
  'New Jersey',
  'New Mexico',
  'New York',
  'North Carolina',
  'North Dakota',
  'Ohio',
  'Oklahoma',
  'Oregon',
  'Pennsylvania',
  'Rhode Island',
  'South Carolina',
  'South Dakota',
  'Tennessee',
  'Texas',
  'Utah',
  'Vermont',
  'Virginia',
  'Washington',
  'West Virginia',
  'Wisconsin',
  'Wyoming'
];

const DonationForm = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [errors, setErrors] = useState({
    vehicleInfo: {},
    pickup: {},
    contact: {}
  });
  const [formData, setFormData] = useState({
    contact: {
      // Test data for future reference
      /*
      title: 'Mr.',
      firstName: 'John',
      lastName: 'Smith',
      phone: '415-555-0123',
      phoneExt: '123',
      altPhone: '415-555-0124',
      altPhoneExt: '456',
      email: 'john.smith@example.com'
      title: '',
      firstName: '',
      lastName: '',
      phone: '',
      phoneExt: '',
      altPhone: '',
      altPhoneExt: '',
      */
      email: ''
    },
    vehicleInfo: {
      // Test data for future reference
      /*
      year: '2022',
      make: 'Toyota',
      model: 'Camry',
      color: 'Silver',
      vin: '1HGCM82633A123456',
      mileage: '50000',
      nameOnTitle: 'John Smith',
      vehicleType: '4door',
      drivetrain: '2wd',
      titleAvailable: 'yes',
      isRunning: 'yes',
      canDrive20Miles: 'yes',
      bodyCondition: 'good',
      mechanicalCondition: 'Regular maintenance, new tires, brakes in good condition',
      */
      year: '',
      make: '',
      model: '',
      color: '',
      vin: '',
      mileage: '',
      nameOnTitle: '',
      vehicleType: '',
      drivetrain: '',
      titleAvailable: '',
      isRunning: '',
      canDrive20Miles: '',
      bodyCondition: '',
      mechanicalCondition: '',
      images: []
    },
    pickup: {
      // Test data for future reference
      /*
      address: '123 Main St',
      city: 'San Francisco',
      state: 'California',
      zip: '94105',
      isCarAtAddress: 'yes',
      */
      address: '',
      city: '',
      state: '',
      zip: '',
      isCarAtAddress: '',
      pickupAddress: '',
      pickupCity: '',
      pickupState: '',
      pickupZip: ''
    },
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validatePhone = (phone) => {
    return /^\d{3}-\d{3}-\d{4}$/.test(phone);
  };

  const validateZip = (zip) => {
    return /^\d{5}(-\d{4})?$/.test(zip);
  };

  const handlePhoneChange = (section, field, value) => {
    // Only allow numbers and dashes
    const cleaned = value.replace(/[^\d-]/g, '');
    
    // Remove extra dashes if user types them
    const numbers = cleaned.replace(/-/g, '');
    
    // Format the number
    let formatted = numbers;
    if (numbers.length > 3 && numbers.length <= 6) {
      formatted = `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    } else if (numbers.length > 6) {
      formatted = `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
    }

    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: formatted
      }
    }));

    // Clear error when user starts typing
    setErrors(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: ''
      }
    }));
  };

  const handleNext = () => {
    let newErrors = { vehicleInfo: {}, pickup: {}, contact: {} };
    let hasErrors = false;

    if (activeStep === 0) {
      // Contact Information validation
      const requiredContactFields = {
        firstName: 'First Name is required',
        lastName: 'Last Name is required',
        phone: 'Phone Number is required',
        email: 'Email is required'
      };

      Object.entries(requiredContactFields).forEach(([field, message]) => {
        if (!formData.contact[field]) {
          newErrors.contact[field] = message;
          hasErrors = true;
        }
      });

      if (formData.contact.phone && !validatePhone(formData.contact.phone)) {
        newErrors.contact.phone = 'Please enter a valid phone number (e.g., 123-456-7890)';
        hasErrors = true;
      }

      if (formData.contact.altPhone && !validatePhone(formData.contact.altPhone)) {
        newErrors.contact.altPhone = 'Please enter a valid phone number (e.g., 123-456-7890)';
        hasErrors = true;
      }

      if (formData.contact.email && !validateEmail(formData.contact.email)) {
        newErrors.contact.email = 'Please enter a properly formed email address';
        hasErrors = true;
      }
    } else if (activeStep === 1) {
      // Vehicle Info validation (now optional)
      if (formData.vehicleInfo.year && (formData.vehicleInfo.year < 1900 || formData.vehicleInfo.year > new Date().getFullYear() + 1)) {
        newErrors.vehicleInfo.year = 'Please enter a valid year';
        hasErrors = true;
      }

      if (formData.vehicleInfo.mileage && formData.vehicleInfo.mileage < 0) {
        newErrors.vehicleInfo.mileage = 'Mileage cannot be negative';
        hasErrors = true;
      }
    } else if (activeStep === 2) {
      // This case is handled by handleSubmit, but kept for structure.
      // Pickup Details validation
      const requiredPickupFields = {
        address: 'Address is required',
        city: 'City is required',
        state: 'State is required',
        zip: 'ZIP is required',
        isCarAtAddress: 'Please select if car is at this address'
      };

      Object.entries(requiredPickupFields).forEach(([field, message]) => {
        if (!formData.pickup[field]) {
          newErrors.pickup[field] = message;
          hasErrors = true;
        }
      });

      if (formData.pickup.zip && !validateZip(formData.pickup.zip)) {
        newErrors.pickup.zip = 'Please enter a valid ZIP code (e.g., 12345 or 12345-6789)';
        hasErrors = true;
      }

      if (formData.pickup.isCarAtAddress === 'no') {
        const requiredExtraFields = {
          pickupAddress: 'Pickup Address is required',
          pickupCity: 'Pickup City is required',
          pickupState: 'Pickup State is required',
          pickupZip: 'Pickup ZIP is required'
        };

        Object.entries(requiredExtraFields).forEach(([field, message]) => {
          if (!formData.pickup[field]) {
            newErrors.pickup[field] = message;
            hasErrors = true;
          }
        });

        if (formData.pickup.pickupZip && !validateZip(formData.pickup.pickupZip)) {
          newErrors.pickup.pickupZip = 'Please enter a valid ZIP code (e.g., 12345 or 12345-6789)';
          hasErrors = true;
        }
      }
    }

    setErrors(newErrors);

    if (!hasErrors) {
      // Scroll to top before moving to next step
      requestAnimationFrame(() => {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth'
        });
      });
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    
    if (formData.vehicleInfo.images.length + files.length > 3) {
      setErrors(prev => ({
        ...prev,
        vehicleInfo: {
          ...prev.vehicleInfo,
          images: 'Maximum 3 images allowed'
        }
      }));
      return;
    }

    const newImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));

    setFormData(prev => ({
      ...prev,
      vehicleInfo: {
        ...prev.vehicleInfo,
        images: [...(prev.vehicleInfo.images || []), ...newImages]
      }
    }));

    setErrors(prev => ({
      ...prev,
      vehicleInfo: {
        ...prev.vehicleInfo,
        images: ''
      }
    }));
  };

  const handleRemoveImage = (index) => {
    setFormData(prev => ({
      ...prev,
      vehicleInfo: {
        ...prev.vehicleInfo,
        images: prev.vehicleInfo.images.filter((_, i) => i !== index)
      }
    }));
  };

  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
    // Clear error when user starts typing
    setErrors(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: ''
      }
    }));
  };

  const steps = ['Contact Information', 'Vehicle Info', 'Pickup Details'];

  const handleBack = () => {
    // Scroll to top before moving to previous step
    requestAnimationFrame(() => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
    });
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSubmit = async () => {
    // Validate final step before submission
    let newErrors = { vehicleInfo: {}, pickup: {}, contact: {} };
    let hasErrors = false;

    // Pickup Details validation
    const requiredPickupFields = {
      address: 'Address is required',
      city: 'City is required',
      state: 'State is required',
      zip: 'ZIP is required',
      isCarAtAddress: 'Please select if car is at this address'
    };

    Object.entries(requiredPickupFields).forEach(([field, message]) => {
      if (!formData.pickup[field]) {
        newErrors.pickup[field] = message;
        hasErrors = true;
      }
    });

    if (formData.pickup.zip && !validateZip(formData.pickup.zip)) {
      newErrors.pickup.zip = 'Please enter a valid ZIP code (e.g., 12345 or 12345-6789)';
      hasErrors = true;
    }

    // No validation for pickupAddress, etc. as it's conditional and handled in handleNext

    setErrors(newErrors);

    if (!hasErrors) {
      try {
        setIsSubmitting(true);
        // Add default title to contact info for backend compatibility
        const submissionData = {
          ...formData,
          contact: {
            ...formData.contact,
            title: 'Mr.' // Set default title
          }
        };
        // Submit regular form data
        const response = await submitDonationForm(submissionData);
        
        if (response.status === 'error') {
          setSnackbar({
            open: true,
            message: response.message || 'An error occurred while submitting your donation.',
            severity: 'error'
          });
          setIsSubmitting(false);
          return;
        }

        // Send PDF worksheet after successful form submission
        const emailResponse = await sendDonationWorksheetPDF(submissionData);
        
        // Check if email sending failed
        if (emailResponse.status === 'error') {
          setSnackbar({
            open: true,
            message: 'Donation submitted but failed to send confirmation email: ' + (emailResponse.message || 'Please contact us.'),
            severity: 'warning'
          });
          setIsSubmitting(false);
          return;
        }

        // Reset form
        setFormData({
          vehicleInfo: { images: [] },
          pickup: {},
          contact: {}
        });

        // Navigate to FAQ page with a success message in the state
        navigate('/faq', { 
          state: { message: 'Thank you for your donation! We will contact you shortly to arrange the pickup.', severity: 'success' } 
        });
      } catch (error) {
        console.error('Error submitting donation:', error);
        // Handle axios error response
        const errorMessage = error.response?.data?.message || 'There was an error submitting your donation. Please try again.';
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error'
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // Background animation elements
  const circles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    size: Math.random() * 100 + 50,
    duration: Math.random() * 20 + 10,
    delay: Math.random() * 5,
  }));

  return (
    <Box
      sx={{
        minHeight: '100vh',
        pt: 12,
        pb: 6,
        position: 'relative',
        overflow: 'hidden',
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
      }}
    >
      {/* Animated background circles */}
      {circles.map((circle) => (
        <Box
          key={circle.id}
          component={motion.div}
          sx={{
            position: 'absolute',
            width: circle.size,
            height: circle.size,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.05)',
            zIndex: 0,
          }}
          animate={{
            y: ['0vh', '100vh'],
            x: ['0vw', '100vw'],
          }}
          transition={{
            duration: circle.duration,
            repeat: Infinity,
            delay: circle.delay,
            ease: 'linear',
          }}
        />
      ))}

      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 4,
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <Typography variant="h4" align="center" gutterBottom>
              Donate Your Vehicle
            </Typography>
            <Typography
              variant="subtitle1"
              align="center"
              color="text.secondary"
              sx={{ mb: 4 }}
            >
              Your vehicle donation can make a real difference in a veteran&apos;s life
            </Typography>

            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            <Box sx={{ mt: 4 }}>
              {activeStep === 1 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <Typography variant="h6" sx={{ mb: 2 }}>Vehicle Information</Typography>
                  <Typography variant="body2" color="error" sx={{ mb: 3 }}>
                    Please fill out as much information as you can. The more details you provide, the better we can assess your donation.
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        fullWidth
                        label="Year"
                        variant="outlined"
                        type="number"
                        value={formData.vehicleInfo.year || ''}
                        onChange={(e) => handleInputChange('vehicleInfo', 'year', e.target.value)}
                        error={!!errors.vehicleInfo.year}
                        helperText={errors.vehicleInfo.year}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Make"
                        variant="outlined"
                        value={formData.vehicleInfo.make || ''}
                        onChange={(e) => handleInputChange('vehicleInfo', 'make', e.target.value)}
                        error={!!errors.vehicleInfo.make}
                        helperText={errors.vehicleInfo.make}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Model"
                        variant="outlined"
                        value={formData.vehicleInfo.model || ''}
                        onChange={(e) => handleInputChange('vehicleInfo', 'model', e.target.value)}
                        error={!!errors.vehicleInfo.model}
                        helperText={errors.vehicleInfo.model}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Color"
                        variant="outlined"
                        value={formData.vehicleInfo.color || ''}
                        onChange={(e) => handleInputChange('vehicleInfo', 'color', e.target.value)}
                        error={!!errors.vehicleInfo.color}
                        helperText={errors.vehicleInfo.color}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="VIN # (Optional)"
                        variant="outlined"
                        value={formData.vehicleInfo.vin || ''}
                        onChange={(e) => handleInputChange('vehicleInfo', 'vin', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Mileage"
                        variant="outlined"
                        type="number"
                        value={formData.vehicleInfo.mileage || ''}
                        onChange={(e) => handleInputChange('vehicleInfo', 'mileage', e.target.value)}
                        error={!!errors.vehicleInfo.mileage}
                        helperText={errors.vehicleInfo.mileage}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Name on Title"
                        variant="outlined"
                        value={formData.vehicleInfo.nameOnTitle || ''}
                        onChange={(e) => handleInputChange('vehicleInfo', 'nameOnTitle', e.target.value)}
                        error={!!errors.vehicleInfo.nameOnTitle}
                        helperText={errors.vehicleInfo.nameOnTitle}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth variant="outlined" error={!!errors.vehicleInfo.vehicleType}>
                        <InputLabel>Vehicle Type</InputLabel>
                        <Select
                          value={formData.vehicleInfo.vehicleType || ''}
                          onChange={(e) => handleInputChange('vehicleInfo', 'vehicleType', e.target.value)}
                          label="Vehicle Type"
                        >
                          <MenuItem value="2 Door">2 Door</MenuItem>
                          <MenuItem value="4 Door">4 Door</MenuItem>
                        </Select>
                        {errors.vehicleInfo.vehicleType && (
                          <FormHelperText>{errors.vehicleInfo.vehicleType}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth variant="outlined" error={!!errors.vehicleInfo.drivetrain}>
                        <InputLabel>Drivetrain</InputLabel>
                        <Select
                          value={formData.vehicleInfo.drivetrain || ''}
                          onChange={(e) => handleInputChange('vehicleInfo', 'drivetrain', e.target.value)}
                          label="Drivetrain"
                        >
                          <MenuItem value="2 Wheel Drive">2 Wheel Drive</MenuItem>
                          <MenuItem value="4 Wheel Drive">4 Wheel Drive</MenuItem>
                        </Select>
                        {errors.vehicleInfo.drivetrain && (
                          <FormHelperText>{errors.vehicleInfo.drivetrain}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Mechanical Condition"
                        variant="outlined"
                        multiline
                        rows={4}
                        placeholder="Example: Vehicle runs great or vehicle does not run may need a jump or starter"
                        value={formData.vehicleInfo.mechanicalCondition || ''}
                        onChange={(e) => handleInputChange('vehicleInfo', 'mechanicalCondition', e.target.value)}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <FormControl component="fieldset" error={!!errors.vehicleInfo.titleAvailable}>
                        <FormLabel component="legend">Title Available?</FormLabel>
                        <RadioGroup
                          row
                          value={formData.vehicleInfo.titleAvailable || ''}
                          onChange={(e) => handleInputChange('vehicleInfo', 'titleAvailable', e.target.value)}
                        >
                          <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                          <FormControlLabel value="no" control={<Radio />} label="No" />
                        </RadioGroup>
                        {errors.vehicleInfo.titleAvailable && (
                          <FormHelperText>{errors.vehicleInfo.titleAvailable}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl component="fieldset" error={!!errors.vehicleInfo.isRunning}>
                        <FormLabel component="legend">Is the vehicle current running?</FormLabel>
                        <RadioGroup
                          row
                          value={formData.vehicleInfo.isRunning || ''}
                          onChange={(e) => handleInputChange('vehicleInfo', 'isRunning', e.target.value)}
                        >
                          <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                          <FormControlLabel value="no" control={<Radio />} label="No" />
                        </RadioGroup>
                        {errors.vehicleInfo.isRunning && (
                          <FormHelperText>{errors.vehicleInfo.isRunning}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl component="fieldset" error={!!errors.vehicleInfo.canDrive20Miles}>
                        <FormLabel component="legend">Can it drive more than 20 miles?</FormLabel>
                        <RadioGroup
                          row
                          value={formData.vehicleInfo.canDrive20Miles || ''}
                          onChange={(e) => handleInputChange('vehicleInfo', 'canDrive20Miles', e.target.value)}
                        >
                          <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                          <FormControlLabel value="no" control={<Radio />} label="No" />
                        </RadioGroup>
                        {errors.vehicleInfo.canDrive20Miles && (
                          <FormHelperText>{errors.vehicleInfo.canDrive20Miles}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl component="fieldset" error={!!errors.vehicleInfo.bodyCondition}>
                        <FormLabel component="legend">Vehicle Body Condition</FormLabel>
                        <RadioGroup
                          row
                          value={formData.vehicleInfo.bodyCondition || ''}
                          onChange={(e) => handleInputChange('vehicleInfo', 'bodyCondition', e.target.value)}
                        >
                          <FormControlLabel value="good" control={<Radio />} label="Good" />
                          <FormControlLabel value="fair" control={<Radio />} label="Fair" />
                          <FormControlLabel value="poor" control={<Radio />} label="Poor" />
                        </RadioGroup>
                        {errors.vehicleInfo.bodyCondition && (
                          <FormHelperText>{errors.vehicleInfo.bodyCondition}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>
                          Vehicle Images (Optional - Max 3)
                        </Typography>
                        <input
                          accept="image/*"
                          type="file"
                          multiple
                          onChange={handleImageUpload}
                          style={{ display: 'none' }}
                          id="vehicle-images-input"
                        />
                        <label htmlFor="vehicle-images-input">
                          <Button
                            component="span"
                            variant="outlined"
                            startIcon={<AddPhotoAlternateIcon />}
                            sx={{ mb: 2 }}
                            disabled={formData.vehicleInfo.images?.length >= 3}
                          >
                            Add Images
                          </Button>
                        </label>
                        {errors.vehicleInfo.images && (
                          <FormHelperText error>{errors.vehicleInfo.images}</FormHelperText>
                        )}
                        <Grid container spacing={2}>
                          {formData.vehicleInfo.images?.map((image, index) => (
                            <Grid item xs={12} sm={4} key={index}>
                              <Box
                                sx={{
                                  position: 'relative',
                                  height: 200,
                                  border: '1px solid #ccc',
                                  borderRadius: 1,
                                  overflow: 'hidden'
                                }}
                              >
                                <img
                                  src={image.preview}
                                  alt={`Vehicle preview ${index + 1}`}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                  }}
                                />
                                <IconButton
                                  sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                    '&:hover': {
                                      backgroundColor: 'rgba(255, 255, 255, 0.9)'
                                    }
                                  }}
                                  onClick={() => handleRemoveImage(index)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    </Grid>
                  </Grid>
                </motion.div>
              )}

              {activeStep === 2 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <Typography variant="h6" sx={{ mb: 2 }}>Pickup Details</Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        required
                        fullWidth
                        label="Address"
                        variant="outlined"
                        value={formData.pickup.address || ''}
                        onChange={(e) => handleInputChange('pickup', 'address', e.target.value)}
                        error={!!errors.pickup.address}
                        helperText={errors.pickup.address}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        required
                        fullWidth
                        label="City"
                        variant="outlined"
                        value={formData.pickup.city || ''}
                        onChange={(e) => handleInputChange('pickup', 'city', e.target.value)}
                        error={!!errors.pickup.city}
                        helperText={errors.pickup.city}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl required fullWidth variant="outlined" error={!!errors.pickup.state}>
                        <InputLabel>State</InputLabel>
                        <Select
                          value={formData.pickup.state || ''}
                          onChange={(e) => handleInputChange('pickup', 'state', e.target.value)}
                          label="State"
                        >
                          {US_STATES.map((state) => (
                            <MenuItem key={state} value={state}>
                              {state}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.pickup.state && (
                          <FormHelperText>{errors.pickup.state}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        required
                        fullWidth
                        label="ZIP"
                        variant="outlined"
                        value={formData.pickup.zip || ''}
                        onChange={(e) => handleInputChange('pickup', 'zip', e.target.value)}
                        error={!!errors.pickup.zip}
                        helperText={errors.pickup.zip}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl required component="fieldset" error={!!errors.pickup.isCarAtAddress}>
                        <FormLabel component="legend">Is the car located at the above address?</FormLabel>
                        <RadioGroup
                          row
                          value={formData.pickup.isCarAtAddress || ''}
                          onChange={(e) => handleInputChange('pickup', 'isCarAtAddress', e.target.value)}
                        >
                          <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                          <FormControlLabel value="no" control={<Radio />} label="No" />
                        </RadioGroup>
                        {errors.pickup.isCarAtAddress && (
                          <FormHelperText>{errors.pickup.isCarAtAddress}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>

                    {formData.pickup.isCarAtAddress === 'no' && (
                      <>
                        <Grid item xs={12}>
                          <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
                            Vehicle Pick Up Address
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            required
                            fullWidth
                            label="Address"
                            variant="outlined"
                            value={formData.pickup.pickupAddress || ''}
                            onChange={(e) => handleInputChange('pickup', 'pickupAddress', e.target.value)}
                            error={!!errors.pickup.pickupAddress}
                            helperText={errors.pickup.pickupAddress}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            required
                            fullWidth
                            label="City"
                            variant="outlined"
                            value={formData.pickup.pickupCity || ''}
                            onChange={(e) => handleInputChange('pickup', 'pickupCity', e.target.value)}
                            error={!!errors.pickup.pickupCity}
                            helperText={errors.pickup.pickupCity}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <FormControl required fullWidth variant="outlined" error={!!errors.pickup.pickupState}>
                            <InputLabel>State</InputLabel>
                            <Select
                              value={formData.pickup.pickupState || ''}
                              onChange={(e) => handleInputChange('pickup', 'pickupState', e.target.value)}
                              label="State"
                            >
                              {US_STATES.map((state) => (
                                <MenuItem key={state} value={state}>
                                  {state}
                                </MenuItem>
                              ))}
                            </Select>
                            {errors.pickup.pickupState && (
                              <FormHelperText>{errors.pickup.pickupState}</FormHelperText>
                            )}
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            required
                            fullWidth
                            label="ZIP"
                            variant="outlined"
                            value={formData.pickup.pickupZip || ''}
                            onChange={(e) => handleInputChange('pickup', 'pickupZip', e.target.value)}
                            error={!!errors.pickup.pickupZip}
                            helperText={errors.pickup.pickupZip}
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>
                </motion.div>
              )}

              {activeStep === 0 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <Typography variant="h6" sx={{ mb: 2 }}>Contact Information</Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        fullWidth
                        label="First Name"
                        variant="outlined"
                        value={formData.contact.firstName || ''}
                        onChange={(e) => handleInputChange('contact', 'firstName', e.target.value)}
                        error={!!errors.contact.firstName}
                        helperText={errors.contact.firstName}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        fullWidth
                        label="Last Name"
                        variant="outlined"
                        value={formData.contact.lastName || ''}
                        onChange={(e) => handleInputChange('contact', 'lastName', e.target.value)}
                        error={!!errors.contact.lastName}
                        helperText={errors.contact.lastName}
                      />
                    </Grid>

                    <Grid item xs={12} sm={8}>
                      <TextField
                        required
                        fullWidth
                        label="Phone (###-###-####)"
                        variant="outlined"
                        value={formData.contact.phone || ''}
                        onChange={(e) => handlePhoneChange('contact', 'phone', e.target.value)}
                        error={!!errors.contact.phone}
                        helperText={errors.contact.phone}
                        inputProps={{ maxLength: 12 }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Phone Ext"
                        variant="outlined"
                        value={formData.contact.phoneExt || ''}
                        onChange={(e) => handleInputChange('contact', 'phoneExt', e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12} sm={8}>
                      <TextField
                        fullWidth
                        label="Alt Phone (###-###-####) (Optional)"
                        variant="outlined"
                        value={formData.contact.altPhone || ''}
                        onChange={(e) => handlePhoneChange('contact', 'altPhone', e.target.value)}
                        error={!!errors.contact.altPhone}
                        helperText={errors.contact.altPhone}
                        inputProps={{ maxLength: 12 }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Alt Phone Ext (Optional)"
                        variant="outlined"
                        value={formData.contact.altPhoneExt || ''}
                        onChange={(e) => handleInputChange('contact', 'altPhoneExt', e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        required
                        fullWidth
                        label="Email"
                        type="email"
                        variant="outlined"
                        value={formData.contact.email || ''}
                        onChange={(e) => handleInputChange('contact', 'email', e.target.value)}
                        error={!!errors.contact.email}
                        helperText={errors.contact.email || '* Confirmation and Tax Deductible Receipt will be sent to this email address'}
                        FormHelperTextProps={{
                          sx: {
                            color: errors.contact.email ? 'error.main' : 'text.secondary',
                            backgroundColor: 'rgba(96, 125, 139, 0.9)',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            marginTop: '4px'
                          }
                        }}
                      />
                    </Grid>
                  </Grid>
                </motion.div>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                <Button
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  variant="outlined"
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={activeStep === steps.length - 1 ? handleSubmit : handleNext}
                  disabled={isSubmitting}
                  sx={{ 
                    position: 'relative',
                    minWidth: '120px',
                    minHeight: '36px',
                    '&.Mui-disabled': {
                      backgroundColor: 'primary.main',
                      color: 'white'
                    }
                  }}
                >
                  {isSubmitting ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress
                        size={20}
                        thickness={5}
                        sx={{
                          color: 'white'
                        }}
                      />
                      <span>Submitting...</span>
                    </Box>
                  ) : (
                    activeStep === steps.length - 1 ? 'Submit' : 'Next'
                  )}
                </Button>
              </Box>
            </Box>
          </Paper>
        </motion.div>

        {/* space */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >

        {/* Impact Stats */}
        {/* <Grid container spacing={3} sx={{ mt: 6 }}>
          {[
            { icon: <DirectionsCarIcon />, title: 'Vehicles Donated', value: '1,234+' },
            { icon: <VolunteerActivismIcon />, title: 'Veterans Helped', value: '5,678+' },
            { icon: <LocalShippingIcon />, title: 'Free Pickups', value: '24/7' },
          ].map((stat, index) => (
            <Grid item xs={12} md={4} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
              >
                <Card
                  sx={{
                    height: '100%',
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Box sx={{ color: theme.palette.primary.main, mb: 1 }}>
                      {stat.icon}
                    </Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {stat.value}
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                      {stat.title}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid> */}
        </motion.div>
      </Container>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DonationForm;
